// apps/landing-page/src/components/nav/NavDropdown.tsx
'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

export interface NavDropdownProps {
  /** Unique key for this dropdown, used to derive the aria-controls id. */
  id: string;
  label: string;
  isOpen: boolean;
  isActive: boolean;
  onToggle: () => void;
  /** Called on outside-click or Escape. Caller owns the actual state (only one
   *  desktop dropdown is meant to be open across the whole nav at a time). */
  onClose: () => void;
  /** Panel width utility class — defaults to the 420px used across the nav. */
  panelWidthClassName?: string;
  children: ReactNode;
}

/**
 * Shared shell for the desktop nav's click-toggle dropdowns (Paket Ujian, Program).
 * Presentational: trigger button + chevron + floating panel, plus the a11y and
 * dismiss behaviour every dropdown needs (aria-expanded/aria-haspopup, outside-click,
 * Escape). Each instance owns its own outside-click listener scoped to its own
 * container ref, so N dropdowns compose without a shared ref list to maintain —
 * unlike the two-ref AND-condition this replaced, which would only have gotten
 * more awkward with a third dropdown.
 */
export function NavDropdown({
  id,
  label,
  isOpen,
  isActive,
  onToggle,
  onClose,
  panelWidthClassName = 'w-[420px]',
  children,
}: NavDropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = `nav-dropdown-panel-${id}`;

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={panelId}
        className={`px-3 py-2 rounded-md text-[15px] font-medium transition-all flex items-center gap-1 ${
          isActive ? 'text-teal-600 bg-teal-50' : 'text-slate-600 hover:text-teal-600 hover:bg-teal-50'
        }`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          id={panelId}
          className={`absolute top-full left-0 mt-2 overflow-hidden bg-white rounded-2xl shadow-xl border border-slate-100 z-10 ${panelWidthClassName}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}
